/**
 * rejectedAlternatives — the reading half of P2 (structural rejected
 * alternatives), pure and unit-tested.
 *
 * PRD: docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md §4.
 * Storage + derivation: migration 702 (`decision_rejected_alternatives`,
 * `record_rejected_alternatives`).
 *
 * The phase converts "we considered X" from a prose claim into a record of
 * what the decision actually beat: the below-cutline items of the locked
 * comparison set at decision time. This module holds the two things that must
 * behave identically on the client and in the migration — WHICH items are
 * below the line, and HOW an absence is described — so that the preview a
 * capturer sees before they commit is the same set the RPC will store.
 *
 * Nothing here writes. The client's derivation is a PREVIEW; the RPC derives
 * again from the pool's own rows, and that second derivation is the record.
 * They agree because the predicate lives here, once.
 */

// ── Below the cutline ────────────────────────────────────────────────────────

/**
 * One member of a locked comparison set.
 *
 * `rank` is 1-indexed in both set shapes (item_pool_memberships.rank is
 * written as `indexOf(id) + 1`; a priority list's ranked array is read with
 * 1-indexed ordinality).
 */
export interface ComparisonSetMember {
  itemId: string;
  rank: number;
  title?: string;
  /** item_pool_memberships.selection_reason where the shape carries one. */
  cutReason?: string | null;
}

/**
 * The two legal values of `decision_rejected_alternatives.comparison_set_kind`,
 * as a runtime list.
 *
 * The column is CHECK-constrained `text`, which generates as `string`, so the
 * service needs an actual list to narrow a read against — a type alone cannot
 * check a value that arrives from the database.
 */
export const COMPARISON_SET_KINDS = ['item_pool', 'priority_set'] as const;

export type ComparisonSetKind = (typeof COMPARISON_SET_KINDS)[number];

export interface ComparisonSet {
  kind: ComparisonSetKind;
  id: string;
  /**
   * A 0-indexed COUNT of the items above the line: items at ranks
   * 1…cutLinePosition are above it. `null` means no cut line was ever placed.
   */
  cutLinePosition: number | null;
  members: ComparisonSetMember[];
}

/** Why a set yielded no structural alternatives — never silently zero. */
export type NoAlternativesReason =
  /** No cut line was placed, so nothing is below it. Not "everything lost". */
  | 'no_cut_line_placed'
  /** A cut line exists and every member is above it. */
  | 'nothing_below_cutline'
  /** The set itself is empty. */
  | 'empty_comparison_set';

export interface BelowCutlineResult {
  members: ComparisonSetMember[];
  /** Present only when `members` is empty. */
  reason: NoAlternativesReason | null;
}

/**
 * The items a decision beat, in rank order.
 *
 * This is the exact predicate migration 702 uses (`rank > cut_line_position`).
 * The NULL cut line is the case worth being careful about: treating "no cut
 * line" as position 0 would declare the entire set rejected, which is the
 * opposite of the truth — nobody has cut anything yet.
 */
export function belowCutline(set: ComparisonSet): BelowCutlineResult {
  if (set.members.length === 0) {
    return { members: [], reason: 'empty_comparison_set' };
  }
  if (set.cutLinePosition === null || set.cutLinePosition === undefined) {
    return { members: [], reason: 'no_cut_line_placed' };
  }

  const cut = set.cutLinePosition;
  const members = set.members
    .filter((m) => m.rank > cut)
    .sort((a, b) => a.rank - b.rank);

  return {
    members,
    reason: members.length === 0 ? 'nothing_below_cutline' : null,
  };
}

const NO_ALTERNATIVES_NARRATION: Record<NoAlternativesReason, string> = {
  no_cut_line_placed:
    'No cut line was placed on this comparison set, so there is nothing it was chosen over.',
  nothing_below_cutline:
    'Every item in the comparison set was above the cut line — this decision displaced nothing in it.',
  empty_comparison_set: 'The comparison set holds no items.',
};

export function narrateNoAlternatives(reason: NoAlternativesReason): string {
  return NO_ALTERNATIVES_NARRATION[reason];
}

// ── Rendering a captured record ──────────────────────────────────────────────

/** A stored row from `decision_rejected_alternatives`, in client shape. */
export interface RejectedAlternative {
  itemId: string;
  title: string | null;
  rankAtDecision: number | null;
  cutLinePositionAtDecision: number | null;
  cutReason: string | null;
  comparisonSetKind: ComparisonSetKind;
  comparisonSetId: string;
}

function displayTitle(alt: RejectedAlternative): string {
  return alt.title?.trim() || 'Untitled item';
}

/**
 * The PRD's render: "chosen over ‹items›, which remained below the cutline for
 * ‹cut reasons›."
 *
 * Two deliberate refusals here. It never invents a cut reason — most rows will
 * have none, and "below the cut line" is itself the honest reason. And it never
 * silently truncates: when the list is long it says how many more there are,
 * because a reader who cannot tell they are seeing a sample is being misled
 * about the size of what was passed over.
 */
export function narrateAlternatives(
  alternatives: RejectedAlternative[],
  options: { maxNamed?: number } = {},
): string {
  if (alternatives.length === 0) return '';

  const maxNamed = options.maxNamed ?? 3;
  const ordered = [...alternatives].sort(
    (a, b) => (a.rankAtDecision ?? Number.MAX_SAFE_INTEGER) - (b.rankAtDecision ?? Number.MAX_SAFE_INTEGER),
  );
  const named = ordered.slice(0, maxNamed).map(displayTitle);
  const remaining = ordered.length - named.length;

  const subject =
    remaining > 0
      ? `${joinList(named)} and ${remaining} other${remaining === 1 ? '' : 's'}`
      : joinList(named);

  const reasons = Array.from(
    new Set(
      ordered
        .map((a) => a.cutReason?.trim())
        .filter((r): r is string => Boolean(r)),
    ),
  );

  if (reasons.length === 0) {
    return `Chosen over ${subject}, which remained below the cut line.`;
  }
  return `Chosen over ${subject}, which remained below the cut line for ${joinList(reasons)}.`;
}

function joinList(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * How a decision's "instead of what" reads: structural, prose, both, or
 * neither.
 *
 * This is what the success metric ("% of decisions with structural rather than
 * prose-only alternatives") counts, and what the commitment view branches on.
 * `prose_only` is not a failure state — it is the honest state of every
 * decision made before P2 and of every alternative that was never an item.
 */
export type AlternativesCoverage = 'structural' | 'structural_and_prose' | 'prose_only' | 'none';

export function coverageOf(args: {
  structuralCount: number;
  prose: string | null | undefined;
}): AlternativesCoverage {
  const hasProse = Boolean(args.prose?.trim());
  const hasStructural = args.structuralCount > 0;

  if (hasStructural && hasProse) return 'structural_and_prose';
  if (hasStructural) return 'structural';
  if (hasProse) return 'prose_only';
  return 'none';
}

export const COVERAGE_LABELS: Record<AlternativesCoverage, string> = {
  structural: 'Verified against the comparison set',
  structural_and_prose: 'Verified against the comparison set, plus outside options',
  prose_only: 'Recorded as prose only',
  none: 'No alternatives recorded',
};
